import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, RefreshCw, Shield, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const MOVEMENT_OPTIONS = [
  { value: 'received', label: 'Stock received' },
  { value: 'returned', label: 'Returned' },
  { value: 'destroyed', label: 'Destroyed' },
  { value: 'stock_check', label: 'Stock check' },
  { value: 'transferred', label: 'Transferred' },
];

const MOVEMENT_LABELS = Object.fromEntries(MOVEMENT_OPTIONS.map(item => [item.value, item.label]));

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-GB');
}

function readApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
}

export default function ControlledDrugRegister({ serviceUserId, readOnly = false }) {
  const { user } = useAuth();
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  const currentUserId = user?.user_id || user?.id || '';
  const [data, setData] = useState({ medications: [], entries: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [witnessingId, setWitnessingId] = useState(null);
  const [witnessChecked, setWitnessChecked] = useState(false);
  const [voidEntry, setVoidEntry] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [form, setForm] = useState({
    medication_id: '',
    entry_type: 'received',
    quantity: '',
    notes: '',
  });

  const load = useCallback(async () => {
    if (!serviceUserId || !token) return;
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(
        `${API}/service-users/${serviceUserId}/cd-register`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 18000 },
      );
      const payload = response.data || {};
      setData({
        medications: Array.isArray(payload.medications) ? payload.medications : [],
        entries: Array.isArray(payload.entries) ? payload.entries : [],
      });
    } catch (err) {
      setData({ medications: [], entries: [] });
      setWitnessingId(null);
      setWitnessChecked(false);
      setLoadError(true);
      toast.error(readApiError(err, 'Could not load the Controlled Drug register'));
    } finally {
      setLoading(false);
    }
  }, [serviceUserId, token]);

  useEffect(() => { load(); }, [load]);

  const pendingByMedication = useMemo(() => {
    const map = new Map();
    for (const entry of data.entries) {
      if (!entry?.is_complete && !entry?.is_voided && !map.has(entry.medication_id)) {
        map.set(entry.medication_id, entry);
      }
    }
    return map;
  }, [data.entries]);

  const balances = useMemo(() => {
    const map = new Map();
    for (const med of data.medications) {
      const latest = data.entries.find(entry =>
        entry.medication_id === med.id && entry.is_complete === true && entry.is_voided !== true
      );
      map.set(med.id, latest ? latest.balance_after : 0);
    }
    return map;
  }, [data.medications, data.entries]);

  const selectedPending = form.medication_id ? pendingByMedication.get(form.medication_id) : null;
  const movementQuantity = Number(form.quantity);
  const quantityValid = form.entry_type === 'stock_check'
    ? Number.isInteger(movementQuantity) && movementQuantity >= 0
    : Number.isInteger(movementQuantity) && movementQuantity > 0;

  const createMovement = async () => {
    if (!form.medication_id) {
      toast.error('Select a controlled medication');
      return;
    }
    if (!quantityValid) {
      toast.error(form.entry_type === 'stock_check' ? 'Enter a whole stock count of 0 or more' : 'Enter a whole quantity greater than 0');
      return;
    }
    if (selectedPending) {
      toast.error('Complete or void the existing pending entry for this medication first');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/cd-entries`,
        {
          medication_id: form.medication_id,
          entry_type: form.entry_type,
          quantity: movementQuantity,
          notes: form.notes.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 18000 },
      );
      toast.success('Controlled Drug entry prepared — a different authorised person must complete the second witness');
      setForm(current => ({ ...current, quantity: '', notes: '' }));
      await load();
    } catch (err) {
      toast.error(readApiError(err, 'Could not create Controlled Drug entry'));
    } finally {
      setSaving(false);
    }
  };

  const confirmWitness = async (entry) => {
    if (!witnessChecked || !entry) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/cd-entries/${entry.id}/witness`,
        { confirmed: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 18000 },
      );
      toast.success('Second witness recorded — ledger entry is complete');
      setWitnessingId(null);
      setWitnessChecked(false);
      await load();
    } catch (err) {
      toast.error(readApiError(err, 'Could not complete second witness'));
    } finally {
      setSaving(false);
    }
  };

  const voidPending = async () => {
    if (!voidEntry || !voidReason.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/cd-entries/${voidEntry.id}/void`,
        { reason: voidReason.trim() },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 18000 },
      );
      toast.success('Pending Controlled Drug entry voided — audit history retained');
      setVoidEntry(null);
      setVoidReason('');
      await load();
    } catch (err) {
      toast.error(readApiError(err, 'Could not void Controlled Drug entry'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14 text-sm text-slate-500">
        <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Loading Controlled Drug register…
      </div>
    );
  }

  if (loadError) {
    return <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">Register unavailable. <Button variant="outline" onClick={load}>Refresh register</Button></div>;
  }

  if (data.medications.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <Shield className="mx-auto h-8 w-8 text-slate-300" />
        <p className="mt-2 text-sm font-semibold text-slate-700">No controlled medications on the prescription list</p>
        <p className="mt-1 text-xs text-slate-500">Mark a medication as Controlled Drug in the prescription list when applicable.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="controlled-drug-register">
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-[#163A70] mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#163A70]">Controlled Drug register</p>
            <p className="mt-1 text-xs text-blue-900">
              Each movement is prepared by one authorised person and remains pending until a different authorised person independently checks and confirms it. Completed entries cannot be edited or deleted here.
            </p>
          </div>
        </div>
      </div>

      {!readOnly && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">Prepare stock movement</h3>
          <p className="mt-1 text-xs text-slate-500">Administration during a care visit uses the worker CD workflow, not this stock-movement form.</p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <select
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={form.medication_id}
              onChange={e => setForm(current => ({ ...current, medication_id: e.target.value }))}
            >
              <option value="">Select medication</option>
              {data.medications.map(med => (
                <option key={med.id} value={med.id}>{med.medication_name} {med.cd_schedule ? `(${med.cd_schedule})` : ''}</option>
              ))}
            </select>
            <select
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={form.entry_type}
              onChange={e => setForm(current => ({ ...current, entry_type: e.target.value }))}
            >
              {MOVEMENT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <input
              type="number"
              min={form.entry_type === 'stock_check' ? 0 : 1}
              step="1"
              value={form.quantity}
              onChange={e => setForm(current => ({ ...current, quantity: e.target.value }))}
              placeholder={form.entry_type === 'stock_check' ? 'Count' : 'Quantity'}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <Button
              onClick={createMovement}
              disabled={saving || !form.medication_id || !quantityValid || !!selectedPending}
              className="bg-[#163A70] text-white hover:bg-[#102d58]"
            >
              {saving ? 'Saving…' : 'Prepare entry'}
            </Button>
          </div>
          <textarea
            rows={2}
            value={form.notes}
            onChange={e => setForm(current => ({ ...current, notes: e.target.value }))}
            placeholder="Optional note / source / destination / destruction context"
            className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          {form.medication_id && (
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                Current completed balance: {balances.get(form.medication_id) ?? 0}
              </span>
              {selectedPending && (
                <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-800">
                  Pending second witness — another movement is locked
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {data.entries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            No Controlled Drug ledger entries yet.
          </div>
        ) : data.entries.map(entry => {
          const isPending = !entry.is_complete && !entry.is_voided;
          const awaitingMar = entry.entry_type === 'administered' && entry.administration_state !== 'recorded';
          const sameFirstWitness = currentUserId && currentUserId === entry.witness_1_id;
          const isWitnessDialog = witnessingId === entry.id;
          return (
            <div key={entry.id} className={`rounded-xl border p-4 shadow-sm ${
              entry.is_voided ? 'border-slate-200 bg-slate-50 opacity-75' :
              isPending || awaitingMar ? 'border-amber-200 bg-amber-50/40' :
              'border-green-200 bg-white'
            }`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{entry.medication_name}</p>
                    <Badge className="bg-[#163A70]/10 text-[#163A70] text-xs">{MOVEMENT_LABELS[entry.entry_type] || entry.entry_type}</Badge>
                    {entry.is_voided ? (
                      <Badge className="bg-slate-200 text-slate-700 text-xs">Voided</Badge>
                    ) : entry.entry_type === 'administered' && entry.administration_state !== 'recorded' ? (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">{entry.is_complete ? 'Witnessed — MAR pending' : 'First record — witness pending'}</Badge>
                    ) : entry.is_complete ? (
                      <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle2 className="mr-1 h-3 w-3" />Complete</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">Pending second witness</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-600">
                    Quantity {entry.quantity} · balance {entry.balance_before} → {entry.balance_after}
                  </p>
                  {entry.entry_type === 'administered' && (
                    <p className="mt-1 text-xs text-slate-700">MAR dose: {entry.administration_date} · {entry.scheduled_time} · {entry.dose_given || 'Historical dose'} · {entry.mar_entry_id ? 'MAR recorded' : 'MAR not yet recorded'}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-500">
                    First witness: {entry.witness_1_name || 'Recorded staff'} · {formatDateTime(entry.witness_1_confirmed_at || entry.created_at)}
                  </p>
                  {entry.witness_2_id && (
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Second witness: {entry.witness_2_name || 'Authorised staff'} · {formatDateTime(entry.witness_2_confirmed_at)}
                    </p>
                  )}
                  {entry.notes && <p className="mt-2 text-xs text-slate-600">{entry.notes}</p>}
                  {entry.is_voided && (
                    <p className="mt-2 text-xs text-slate-600">
                      Voided by {entry.voided_by_name || 'manager'}: {entry.void_reason}
                    </p>
                  )}
                </div>

                {isPending && !readOnly && (
                  <div className="flex flex-wrap gap-2">
                    {!entry.worker_handoff && <Button
                      size="sm"
                      variant="outline"
                      disabled={sameFirstWitness}
                      onClick={() => {
                        setWitnessingId(entry.id);
                        setWitnessChecked(false);
                      }}
                    >
                      Second witness
                    </Button>}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setVoidEntry(entry);
                        setVoidReason('');
                      }}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />Void pending
                    </Button>
                  </div>
                )}
              </div>

              {sameFirstWitness && isPending && !readOnly && (
                <p className="mt-3 text-xs font-medium text-amber-800">
                  You prepared this entry. A different authorised person must complete the second witness.
                </p>
              )}
              {entry.worker_handoff && isPending && <p className="mt-3 text-xs text-blue-900">The second worker witnesses this exact dose from their own authenticated, clocked-in care visit.</p>}

              {isWitnessDialog && isPending && (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <label className="flex items-start gap-2 text-xs text-blue-950">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={witnessChecked}
                      onChange={e => setWitnessChecked(e.target.checked)}
                    />
                    <span>
                      I independently checked the medication, movement, quantity and resulting balance and confirm this entry is accurate.
                    </span>
                  </label>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setWitnessingId(null);
                        setWitnessChecked(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={!witnessChecked || saving || sameFirstWitness}
                      onClick={() => confirmWitness(entry)}
                      className="bg-[#163A70] text-white hover:bg-[#102d58]"
                    >
                      {saving ? 'Confirming…' : 'Confirm second witness'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {voidEntry && (
        <div className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/45 p-4" onClick={() => setVoidEntry(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
              <div>
                <h3 className="text-base font-semibold text-slate-900">Void pending CD entry?</h3>
                <p className="mt-1 text-xs text-slate-600">
                  This does not delete history. Use it only when the pending entry is wrong and must be replaced.
                </p>
              </div>
            </div>
            <textarea
              rows={3}
              value={voidReason}
              onChange={e => setVoidReason(e.target.value)}
              placeholder="Reason for voiding *"
              className="mt-4 w-full rounded-lg border border-red-200 px-3 py-2 text-sm"
            />
            <div className="mt-4 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setVoidEntry(null)}>Cancel</Button>
              <Button
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                disabled={saving || !voidReason.trim()}
                onClick={voidPending}
              >
                {saving ? 'Voiding…' : 'Void pending entry'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
